import type { TaskCadence } from "~/api/types";

export const WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const CADENCE_OPTIONS: { value: TaskCadence; label: string }[] = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export function todayIso(date = new Date()) {
  return toIsoDate(date);
}

export function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseIsoDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

function addDays(date: Date, amount: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function lastDayOfMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function setMonthDay(date: Date, monthDay: number) {
  const day = Math.min(
    monthDay,
    lastDayOfMonth(date.getFullYear(), date.getMonth()),
  );
  return new Date(date.getFullYear(), date.getMonth(), day);
}

export type CadenceInput = {
  cadence: TaskCadence;
  weekday?: number | null;
  monthDay?: number | null;
  from?: Date;
};

/** Next due date from today (or `from`) for a newly created or rolled-forward task. */
export function nextDueOn({
  cadence,
  weekday,
  monthDay,
  from = new Date(),
}: CadenceInput): string {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());

  if (cadence === "once" || cadence === "daily") {
    return toIsoDate(start);
  }

  if (cadence === "weekly") {
    const target = weekday ?? start.getDay();
    const delta = (target - start.getDay() + 7) % 7;
    return toIsoDate(addDays(start, delta));
  }

  if (cadence === "monthly") {
    const day = monthDay ?? start.getDate();
    let candidate = setMonthDay(start, day);
    if (candidate < start) {
      candidate = setMonthDay(
        new Date(start.getFullYear(), start.getMonth() + 1, 1),
        day,
      );
    }
    return toIsoDate(candidate);
  }

  const month = start.getMonth();
  const day = monthDay ?? start.getDate();
  let candidate = setMonthDay(new Date(start.getFullYear(), month, 1), day);
  if (candidate < start) {
    candidate = setMonthDay(new Date(start.getFullYear() + 1, month, 1), day);
  }
  return toIsoDate(candidate);
}

/** Advance due_on after completing a recurring task on `completedOn`. */
export function rollDueOn(
  input: CadenceInput & { dueOn: string; completedOn: string },
): string {
  const completed = parseIsoDate(input.completedOn);

  if (input.cadence === "once") return input.dueOn;
  if (input.cadence === "daily") return toIsoDate(addDays(completed, 1));
  if (input.cadence === "weekly") return toIsoDate(addDays(completed, 7));
  if (input.cadence === "monthly") {
    const day = input.monthDay ?? parseIsoDate(input.dueOn).getDate();
    return toIsoDate(
      setMonthDay(
        new Date(completed.getFullYear(), completed.getMonth() + 1, 1),
        day,
      ),
    );
  }
  const due = parseIsoDate(input.dueOn);
  return toIsoDate(
    setMonthDay(
      new Date(completed.getFullYear() + 1, due.getMonth(), 1),
      input.monthDay ?? due.getDate(),
    ),
  );
}

export function cadenceLabel(
  cadence: TaskCadence,
  weekday?: number | null,
  monthDay?: number | null,
) {
  if (cadence === "once") return "Once";
  if (cadence === "daily") return "Daily";
  if (cadence === "weekly") {
    const day = weekday != null ? WEEKDAY_LABELS[weekday] : null;
    return day ? `Weekly on ${day}` : "Weekly";
  }
  if (cadence === "monthly") {
    return monthDay ? `Monthly on the ${ordinal(monthDay)}` : "Monthly";
  }
  return monthDay ? `Yearly on the ${ordinal(monthDay)}` : "Yearly";
}

function ordinal(value: number) {
  const remainder = value % 10;
  const teens = value % 100;
  if (teens >= 11 && teens <= 13) return `${value}th`;
  if (remainder === 1) return `${value}st`;
  if (remainder === 2) return `${value}nd`;
  if (remainder === 3) return `${value}rd`;
  return `${value}th`;
}

export function daysBetween(fromIso: string, toIso: string) {
  const from = parseIsoDate(fromIso);
  const to = parseIsoDate(toIso);
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

export function addDaysIso(iso: string, amount: number) {
  return toIsoDate(addDays(parseIsoDate(iso), amount));
}

export function expectedOccurrences(
  cadence: TaskCadence,
  windowStart: string,
  windowEnd: string,
) {
  const days = daysBetween(windowStart, windowEnd) + 1;
  if (cadence === "once") return 1;
  if (cadence === "daily") return Math.max(days, 0);
  if (cadence === "weekly") return Math.max(Math.ceil(days / 7), 0);
  if (cadence === "monthly") return Math.max(Math.ceil(days / 30), 0);
  return Math.max(Math.ceil(days / 365), 0);
}

export function currentStreak(
  completedOn: string[],
  cadence: TaskCadence,
  today = todayIso(),
) {
  const dates = [...new Set(completedOn)].sort();
  if (dates.length === 0) return 0;

  const stepDays =
    cadence === "weekly"
      ? 7
      : cadence === "monthly"
        ? 30
        : cadence === "yearly"
          ? 365
          : 1;

  let cursor = today;
  if (!dates.includes(cursor)) {
    cursor = addDaysIso(today, -stepDays);
    if (!dates.includes(cursor)) return 0;
  }

  let streak = 0;
  const set = new Set(dates);
  while (set.has(cursor)) {
    streak += 1;
    cursor = addDaysIso(cursor, -stepDays);
  }
  return streak;
}

export const LIST_COLORS = [
  "#81B29A",
  "#E07A5F",
  "#1B2838",
  "#6B8CAE",
  "#C9A227",
] as const;
